/*
 * Combobox: searchable single select (servers, long lists). Filters by label and `keywords`.
 *   <Combobox value={serverId} onValueChange={setServerId}
 *     options={servers.map((s) => ({ value: s.id, label: s.name, description: s.hostname, keywords: [s.hostname] }))} />
 */
import { CaretUpDown, Check, MagnifyingGlass } from '@phosphor-icons/react'
import { Command } from 'cmdk'
import { Popover } from 'radix-ui'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { controlClasses, controlHeight } from './control'
import { useFieldControl } from './Field'

export type ComboboxOption = {
  value: string
  label: string
  description?: ReactNode
  icon?: ReactNode
  keywords?: string[]
  disabled?: boolean
}

export function Combobox({
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled,
  className,
}: {
  value?: string
  onValueChange: (v: string) => void
  options: ComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
}) {
  const { t } = useTranslation('common')
  const field = useFieldControl()
  const [open, setOpen] = useState(false)
  const selected = options.find((o) => o.value === value)
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        id={field?.id}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-describedby={field?.describedBy}
        aria-invalid={field?.invalid || undefined}
        className={cn(
          controlClasses,
          controlHeight,
          'inline-flex items-center gap-2 px-3 text-start',
          className,
        )}
      >
        {selected?.icon ? <span className="text-subtle [&_svg]:size-[18px]">{selected.icon}</span> : null}
        <span className={cn('min-w-0 flex-1 truncate', !selected && 'text-subtle')}>
          {selected?.label ?? placeholder ?? t('ui.select.placeholder')}
        </span>
        <CaretUpDown className="size-4 shrink-0 text-subtle" aria-hidden="true" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          className="z-50 w-[max(var(--radix-popover-trigger-width),16rem)] overflow-hidden rounded-2xl bg-surface shadow-pop ring-1 ring-hairline data-[state=open]:animate-pop-in"
        >
          <Command loop className="flex flex-col">
            <div className="flex items-center gap-2 border-b border-hairline px-3">
              <MagnifyingGlass className="size-4 text-subtle" aria-hidden="true" />
              <Command.Input
                placeholder={searchPlaceholder ?? t('ui.combobox.search')}
                className="h-11 flex-1 bg-transparent text-14 text-ink outline-none placeholder:text-subtle"
              />
            </div>
            <Command.List className="max-h-72 scrollbar-thin overflow-y-auto p-1.5">
              <Command.Empty className="px-3 py-6 text-center text-13 text-subtle">
                {emptyText ?? t('ui.combobox.empty')}
              </Command.Empty>
              {options.map((o) => (
                <Command.Item
                  key={o.value}
                  value={o.value}
                  keywords={[o.label, ...(o.keywords ?? [])]}
                  disabled={o.disabled}
                  onSelect={() => {
                    onValueChange(o.value)
                    setOpen(false)
                  }}
                  className="flex min-h-10 cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2 text-14 text-ink outline-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-surface-tint"
                >
                  {o.icon ? <span className="text-subtle [&_svg]:size-[18px]">{o.icon}</span> : null}
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{o.label}</span>
                    {o.description ? (
                      <span className="truncate text-12 text-subtle">{o.description}</span>
                    ) : null}
                  </span>
                  {o.value === value ? (
                    <Check className="size-4 text-brand-600" weight="bold" aria-hidden="true" />
                  ) : null}
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
